import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { EmrStatusEnum, ProjectStatusEnum } from '~/api/types';
import { useGetEmrsQuery, useGetProjectQuery } from '~/api/v2/hooks';

export enum ProjectActionTypeEnum {
  EMR_STUDIO = 'emrStudio',
  EMR_CLUSTER = 'emrCluster',
  WORKBENCH = 'workbench',
  ETL_JOB = 'etlJob',
}

interface UseProjectActionStateResult {
  isDisabledAction: boolean;
  tooltipMessage: string;
}

const actionLabelMap: Record<ProjectActionTypeEnum, string> = {
  [ProjectActionTypeEnum.EMR_STUDIO]: 'Studio creation',
  [ProjectActionTypeEnum.EMR_CLUSTER]: 'Cluster creation',
  [ProjectActionTypeEnum.WORKBENCH]: 'Workbench creation',
  [ProjectActionTypeEnum.ETL_JOB]: 'Job scheduling',
};

const useProjectActionState = ({ actionType }: { actionType: ProjectActionTypeEnum }): UseProjectActionStateResult => {
  const projectId = +useParams().projectid!;
  const { data: projectData } = useGetProjectQuery({ projectId }, { skip: !projectId });
  const { data: emrData } = useGetEmrsQuery({ projectId }, { skip: !projectId });

  return useMemo(() => {
    const actionLabel = actionLabelMap[actionType];

    if (actionType === ProjectActionTypeEnum.EMR_CLUSTER && emrData?.status === EmrStatusEnum.PENDING) {
      return {
        isDisabledAction: true,
        tooltipMessage: `${actionLabel} is unavailable while EMR Studio is in pending status.`,
      };
    }

    if (actionType === ProjectActionTypeEnum.ETL_JOB && !emrData) {
      return {
        isDisabledAction: true,
        tooltipMessage: `${actionLabel} is unavailable because EMR Studio has not been created yet.`,
      };
    }

    if (projectData?.status === ProjectStatusEnum.INACTIVE) {
      return {
        isDisabledAction: true,
        tooltipMessage: `${actionLabel} is unavailable because this project is inactive.`,
      };
    }

    if (!projectData?.wbs_code_is_active && !projectData?.cost_center_is_active) {
      return {
        isDisabledAction: true,
        tooltipMessage: 'Invalid WBS or cost center code associated with the project. Please update to continue',
      };
    }

    return {
      isDisabledAction: false,
      tooltipMessage: '',
    };
  }, [actionType]);
};

export default useProjectActionState;
